import { Alert, Box, CircularProgress } from '@mui/material'
import { useEffect, useMemo, useRef, useState, type ComponentType } from 'react'
import {
  getInlineEmbeddingMode,
  type AssetTypeDefinition,
  type AssetRenderComponentProps,
  type InlineFeatureComponentProps,
  type ReactAssetRuntime,
  type RenderData,
} from '../assetTypes'
import type { AssetHandle } from '../assetTypes'
import type { ParamChangeEvent, ParamChangeListener, ParamValues } from '../assetParamsBus'
import { cloneJsonObject, isJsonValueEqual, normalizeJsonObject } from '../shared/jsonValues'

export type ResolvedInlineFeature = {
  feature: string
  assetType?: AssetTypeDefinition
  content: string
  renderData?: RenderData
}

type InlineFeatureResolver = (feature: string, params: ParamValues) => Promise<ResolvedInlineFeature>

type InlineFeatureHostProps = InlineFeatureComponentProps & {
  resolveFeature: InlineFeatureResolver
  inlineFeatureComponent: ComponentType<InlineFeatureComponentProps>
  getAssetHandle: (ref: string) => AssetHandle
  entities?: ReactAssetRuntime['entities']
  ui: ReactAssetRuntime['ui']
  onLog?: (message: string) => void
}

type InlineFeatureStatus = 'loading' | 'ready' | 'error'

const createInlineInstanceId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `inline-feature-${crypto.randomUUID()}`
  }
  return `inline-feature-${Math.random().toString(36).slice(2)}`
}

const getChangedKeys = (previousValues: ParamValues, nextValues: ParamValues) => {
  const keys = new Set([...Object.keys(previousValues), ...Object.keys(nextValues)])
  const changedKeys: string[] = []
  keys.forEach((key) => {
    if (!isJsonValueEqual(previousValues[key], nextValues[key])) {
      changedKeys.push(key)
    }
  })
  return changedKeys
}

const InlineFeatureHost = ({
  feature,
  params,
  navigation,
  resolveFeature,
  inlineFeatureComponent,
  getAssetHandle,
  entities,
  ui,
  onLog,
}: InlineFeatureHostProps) => {
  const [status, setStatus] = useState<InlineFeatureStatus>('loading')
  const [resolvedFeature, setResolvedFeature] = useState<ResolvedInlineFeature | null>(null)
  const [error, setError] = useState<string | null>(null)
  const normalizedParams = useMemo(() => normalizeJsonObject(params), [params])
  const paramsRef = useRef<ParamValues>(cloneJsonObject(normalizedParams))
  const paramsRevisionRef = useRef(1)
  const paramsListenersRef = useRef<Set<ParamChangeListener>>(new Set())
  const instanceIdRef = useRef(createInlineInstanceId())

  useEffect(() => {
    const nextParams = cloneJsonObject(normalizedParams)
    const previousParams = paramsRef.current
    const changedKeys = getChangedKeys(previousParams, nextParams)
    if (changedKeys.length === 0) return

    paramsRef.current = nextParams
    paramsRevisionRef.current += 1
    const event: ParamChangeEvent = {
      instanceId: instanceIdRef.current,
      params: cloneJsonObject(nextParams),
      previousParams: cloneJsonObject(previousParams),
      changedKeys,
      revision: paramsRevisionRef.current,
      isInitial: false,
    }
    paramsListenersRef.current.forEach((listener) => listener(event))
  }, [normalizedParams])

  useEffect(() => {
    let cancelled = false
    setStatus('loading')
    setError(null)

    resolveFeature(feature, normalizedParams)
      .then((feature) => {
        if (cancelled) return
        const assetType = feature.assetType
        const embeddingMode = getInlineEmbeddingMode(assetType)
        if (!embeddingMode) {
          throw new Error(
            `Inline embedding only supports inline-embeddable assets. Received "${assetType?.displayName ?? 'unknown'}".`
          )
        }
        if (!assetType?.renderComponent) {
          throw new Error(`Asset type "${assetType?.displayName ?? 'unknown'}" does not provide a React renderer.`)
        }
        setResolvedFeature(feature)
        setStatus('ready')
      })
      .catch((nextError) => {
        if (cancelled) return
        const message = nextError instanceof Error ? nextError.message : String(nextError)
        setResolvedFeature(null)
        setError(message)
        setStatus('error')
        onLog?.(`Inline asset load error: ${message}`)
      })

    return () => {
      cancelled = true
    }
  }, [feature, normalizedParams, onLog, resolveFeature])

  const childRuntime = useMemo<ReactAssetRuntime>(() => {
    return {
      instanceId: instanceIdRef.current,
      entity: resolvedFeature?.renderData?.entity,
      assets: {
        get: getAssetHandle,
      },
      entities,
      params: {
        get: () => cloneJsonObject(paramsRef.current),
        subscribe: (listener) => {
          paramsListenersRef.current.add(listener)
          const currentParams = cloneJsonObject(paramsRef.current)
          const revision = paramsRevisionRef.current
          queueMicrotask(() => {
            listener({
              instanceId: instanceIdRef.current,
              params: currentParams,
              previousParams: null,
              changedKeys: Object.keys(currentParams),
              revision,
              isInitial: true,
            })
          })
          return () => {
            paramsListenersRef.current.delete(listener)
          }
        },
      },
      navigation,
      features: {
        Inline: inlineFeatureComponent,
      },
      log: (message) => {
        onLog?.(String(message))
      },
      ui,
    }
  }, [entities, getAssetHandle, inlineFeatureComponent, navigation, onLog, resolvedFeature?.renderData?.entity, ui])

  if (status === 'loading') {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', height: '100%', width: '100%' }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (status === 'error' || !resolvedFeature) {
    return <Alert severity="error">{error ?? 'Failed to render inline asset.'}</Alert>
  }

  const InlineAssetRenderer = resolvedFeature.assetType?.renderComponent as
    | ComponentType<AssetRenderComponentProps>
    | undefined
  if (!InlineAssetRenderer) {
    return (
      <Alert severity="error">
        {`Asset type "${resolvedFeature.assetType?.displayName ?? 'unknown'}" does not provide a React renderer.`}
      </Alert>
    )
  }

  const embeddingMode = getInlineEmbeddingMode(resolvedFeature.assetType)

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <InlineAssetRenderer
        content={resolvedFeature.content}
        trellis={embeddingMode === 'trellis' ? childRuntime : undefined}
        renderData={resolvedFeature.renderData}
        layout="fill"
        showLog={false}
        onLog={onLog}
      />
    </Box>
  )
}

export default InlineFeatureHost
